import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TaskChart from './TaskChart';
import { usePoints } from '../contexts/PointsContext';

const { FiPlus, FiCheckCircle, FiClock, FiTrendingUp, FiAlertCircle, FiAward, FiFolders, FiBarChart2, FiInfo } = FiIcons;

const Dashboard = ({ tasks, projects, onAddTask, onAddProject, currentProjectId, setFilter, setCurrentProjectId }) => {
  const { points, pointsBreakdown } = usePoints();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Filter tasks by project if a project is selected
  const filteredTasks = currentProjectId
    ? tasks.filter(task => task.projectId === currentProjectId)
    : tasks;

  const totalTasks = filteredTasks.length;
  const completedTasks = filteredTasks.filter(task => task.completed).length;
  const pendingTasks = filteredTasks.filter(task => !task.completed).length;
  const highPriorityTasks = filteredTasks.filter(task => task.priority === 'high' && !task.completed).length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const recentTasks = filteredTasks
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  // Handle navigation to filtered task views
  const navigateToTasksWithFilter = (filter, projectId = null) => {
    if (projectId !== undefined && projectId !== currentProjectId) {
      setCurrentProjectId(projectId);
    }

    if (filter) {
      setFilter(filter);
    }

    navigate('/tasks');
  };

  const stats = [
    {
      title: t('dashboard.totalTasks'),
      value: totalTasks,
      icon: FiTrendingUp,
      color: 'blue',
      bgColor: 'bg-blue-50 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      onClick: () => navigateToTasksWithFilter('all')
    },
    {
      title: t('dashboard.completed'),
      value: completedTasks,
      icon: FiCheckCircle,
      color: 'green',
      bgColor: 'bg-green-50 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400',
      onClick: () => navigateToTasksWithFilter('completed')
    },
    {
      title: t('dashboard.pending'),
      value: pendingTasks,
      icon: FiClock,
      color: 'yellow',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/30',
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      onClick: () => navigateToTasksWithFilter('pending')
    },
    {
      title: t('dashboard.highPriority'),
      value: highPriorityTasks,
      icon: FiAlertCircle,
      color: 'red',
      bgColor: 'bg-red-50 dark:bg-red-900/30',
      iconColor: 'text-red-600 dark:text-red-400',
      onClick: () => navigateToTasksWithFilter('high')
    }
  ];

  // Get current project name
  const getCurrentProjectName = () => {
    if (!currentProjectId) return t('navigation.allProjects');
    const project = projects.find(p => p.id === currentProjectId);
    return project ? project.name : t('navigation.allProjects');
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">{t('dashboard.title')}</h1>
          <p className="text-slate-600 dark:text-slate-400">
            {currentProjectId ? t('dashboard.project', { name: getCurrentProjectName() }) : t('dashboard.welcome')}
          </p>
          </div>
        </div>

        {/* Points Display */}
        <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl shadow-md">
          <SafeIcon icon={FiAward} className="text-yellow-300 text-xl" />
          <div>
            <div className="flex items-center gap-1 text-sm opacity-90">
              <span>{t('navigation.points')}</span>
              <SafeIcon
                icon={FiInfo}
                className="cursor-help"
                title={t('navigation.pointsTooltip', {
                  timeUnits: pointsBreakdown.timeUnits,
                  points
                })}
              />
            </div>
            <div className="text-xl font-bold">{points}</div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`${stat.bgColor} p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-soft hover:shadow-medium transition-shadow cursor-pointer`}
            onClick={stat.onClick}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 dark:text-slate-400 text-sm font-medium mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <SafeIcon icon={stat.icon} className={`text-xl ${stat.iconColor}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">{t('dashboard.taskProgress')}</h2>
          <TaskChart tasks={filteredTasks} />
          <div className="mt-4 text-center">
            <span className="text-2xl font-bold text-slate-800 dark:text-white">{completionRate}%</span>
            <p className="text-slate-600 dark:text-slate-400 text-sm">{t('dashboard.completionRate')}</p>
          </div>
        </motion.div>

        {/* Recent Tasks */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white">{t('dashboard.recentTasks')}</h2>
            <Link
              to="/tasks"
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
            >
              {t('dashboard.viewAll')}
            </Link>
          </div>
          {recentTasks.length > 0 ? (
            <div className="space-y-3">
              {recentTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                  onClick={() => {
                    // Navigate to tasks with the specific project selected
                    setCurrentProjectId(task.projectId);
                    navigate('/tasks');
                  }}
                >
                  <div
                    className={`w-3 h-3 rounded-full ${
                      task.priority === 'high'
                        ? 'bg-red-500'
                        : task.priority === 'medium'
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`font-medium truncate ${
                        task.completed
                          ? 'text-slate-500 dark:text-slate-400 line-through'
                          : 'text-slate-800 dark:text-white'
                      }`}
                    >
                      {task.title}
                    </p>
                    <p className="text-slate-500 dark:text-slate-400 text-sm truncate">{task.description}</p>
                  </div>
                  {task.completed && (
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 text-lg" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <SafeIcon icon={FiClock} className="text-4xl text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">{t('dashboard.noTasks')}</p>
              <motion.button
                onClick={onAddTask}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 mx-auto hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} />
                {t('dashboard.getStarted')}
              </motion.button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Quick Actions Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Projects Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <SafeIcon icon={FiFolders} className="text-xl text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-white">{t('navigation.projects')}</h2>
            </div>
            <span className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-1 rounded-full text-xs font-medium">
              {projects.length}
            </span>
          </div>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {t('projects.title')}
          </p>
          <div className="flex justify-between">
            <Link
              to="/projects"
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            >
              {t('dashboard.viewAll')}
            </Link>
            <motion.button
              onClick={onAddProject}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={FiPlus} />
              {t('projects.createProject')}
            </motion.button>
          </div>
        </motion.div>

        {/* Gantt Chart Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                <SafeIcon icon={FiBarChart2} className="text-xl text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-white">{t('navigation.gantt')}</h2>
            </div>
          </div>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {t('gantt.title')}
          </p>
          <Link
            to="/gantt"
            className="block w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors text-center"
          >
            {t('dashboard.viewAll')}
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
